import Phaser from 'phaser';
import { bus, Events } from '@/core/events';
import { FixedStepper } from '@/core/clock';
import { SeededRng } from '@/core/rng';
import type { GameContext } from '@/core/context';
import type { Settings, WeatherState } from '@/core/types';
import type { IEffectsDirector, IWorldScene, MapData } from '@/core/types/contracts';
import { audio } from '@/audio';
import { HazeLayer, LanternLayer, LeafLayer, LightningLayer, RainLayer, WaterLayer, type FxLayer, type View } from './layers';
import { applyReduceMotion, type WeatherIntensity } from './intensity';
import { ensureFxTextures } from './textures';
import { ONE_SHOTS, type FxHost, type OneShot } from './oneshots';
import { WeatherTrack } from './weatherTrack';

export interface EffectsLayerToggles { rain?: boolean; leaves?: boolean; water?: boolean; lantern?: boolean; haze?: boolean; lightning?: boolean }
export interface EffectsOptions {
  seed?: number | string;
  reduceMotion?: boolean;
  /** SFX sink (defaults to the audio director). */
  sfx?: FxHost['sfx'];
  duck?: FxHost['duck'];
  layers?: EffectsLayerToggles;
}
export interface EffectsState {
  attached: boolean;
  weather: { to: WeatherState; t: number };
  intensity: WeatherIntensity | null;
  reduceMotion: boolean;
  active: string[];
  held: string[];
  layers: number;
  flashes: number;
  steps: number;
  /** Live particle counts / accumulators from every layer and running one-shot (drops, splashes, leaves, ripples, blobs…). */
  particles: Record<string, number>;
}

/** Scene subset the director needs; a full IWorldScene satisfies it, a bare Phaser.Scene degrades to "no map". */
type SceneLike = Phaser.Scene & Partial<Pick<IWorldScene, 'map' | 'weather'>>;

/**
 * Weather layers (rain + splashes, wind leaves, water shimmer + ripples, lantern glow, heat haze,
 * lightning + thunder) bound to one scene, driven by `weather:change`, plus the one-shot registry.
 * Fixed-step, integer pixels, SeededRng only; every listener/object is removed on detach/shutdown.
 */
export class EffectsDirector implements IEffectsDirector {
  private scene: SceneLike | null = null;
  private layers: FxLayer[] = [];
  private lightningLayer: LightningLayer | null = null;
  private track = new WeatherTrack('sultry');
  private readonly stepper = new FixedStepper();
  private readonly rng: SeededRng;
  private readonly shotRng: SeededRng;
  private reduce: boolean;
  private readonly sfxFn: FxHost['sfx'];
  private readonly duckFn: FxHost['duck'];
  private readonly toggles: EffectsLayerToggles;
  private readonly active: { shot: OneShot; name: string; resolve: () => void }[] = [];
  private readonly held: { shot: OneShot; name: string }[] = [];
  readonly shared = new Map<string, unknown>();
  private lastIntensity: WeatherIntensity | null = null;
  private readonly warned = new Set<string>();
  private flashes = 0;
  private readonly onWeather = (p: unknown) => { const e = p as { state: WeatherState; t: number } | null; if (e && typeof e.state === 'string') this.track.apply({ state: e.state, t: typeof e.t === 'number' ? e.t : 1 }); };
  private readonly onSettings = (s: unknown) => { const st = s as Partial<Settings> | null; if (st && typeof st.reduceMotion === 'boolean') this.setReduceMotion(st.reduceMotion); };
  private readonly onUpdate = (_time: number, delta: number) => this.stepper.tick(delta, (dt) => this.step(dt));
  private readonly onShutdown = () => this.detach();

  constructor(opts: EffectsOptions = {}) {
    this.rng = new SeededRng(opts.seed ?? 1157).fork('effects');
    this.shotRng = this.rng.fork('oneshots');
    this.reduce = opts.reduceMotion ?? false;
    this.sfxFn = opts.sfx ?? ((key, o) => audio().sfx(key, o));
    this.duckFn = opts.duck ?? ((amount, ms) => audio().duck(amount, ms));
    this.toggles = opts.layers ?? {};
  }

  // ------------------------------------------------------------------ IEffectsDirector
  attach(scene: IWorldScene | Phaser.Scene): void {
    const s = scene as SceneLike;
    if (this.scene === s) return;
    if (this.scene) this.detach();
    this.scene = s;
    try { ensureFxTextures(s); } catch (e) { console.warn('[effects] textures failed', e); }
    const map: Partial<MapData> = s.map ?? {};
    const water = map.water ?? [], shelter = map.rainShelter ?? [], lights = map.lights ?? [];
    const ctx = s.registry?.get('ctx') as GameContext | undefined;
    if (ctx && typeof ctx.settings?.reduceMotion === 'boolean') this.reduce = ctx.settings.reduceMotion;
    const initial = (typeof s.weather === 'string' ? s.weather : ctx?.weather) ?? 'sultry';
    this.track = new WeatherTrack(initial);
    // A scene without map data (memory stages, battle, showcases of other modules) gets NO weather layers
    // unless a toggle opts in explicitly: a winter sickroom must not inherit the harbor's heat haze or leaves.
    // One-shots work either way. With a map, every layer is on unless toggled off.
    const hasMap = !!s.map;
    const on = (k: keyof EffectsLayerToggles) => (hasMap ? this.toggles[k] !== false : this.toggles[k] === true);
    const layers: FxLayer[] = [];
    const tryAdd = (name: string, fn: () => FxLayer | null) => {
      try {
        const layer = fn();
        if (layer) layers.push(layer);
      } catch (e) {
        console.warn(`[effects] layer "${name}" setup failed`, e);
      }
    };
    if (on('water') && water.length) tryAdd('water', () => new WaterLayer(s, this.rng.fork('water'), water));
    if (on('lantern') && lights.length) tryAdd('lantern', () => new LanternLayer(s, this.rng.fork('lantern'), lights));
    if (on('rain')) tryAdd('rain', () => new RainLayer(s, this.rng.fork('rain'), shelter, water));
    if (on('leaves')) tryAdd('leaves', () => new LeafLayer(s, this.rng.fork('leaves')));
    if (on('haze')) tryAdd('haze', () => new HazeLayer(s));
    if (on('lightning')) tryAdd('lightning', () => {
      this.lightningLayer = new LightningLayer(s, this.rng.fork('lightning'), (v) => { this.flashes++; this.sfxFn('sfx-thunder', { volume: v }); });
      return this.lightningLayer;
    });
    this.layers = layers;
    for (const l of layers) l.setReduceMotion?.(this.reduce);
    bus.on(Events.WEATHER_CHANGE, this.onWeather);
    bus.on(Events.SETTINGS_CHANGE, this.onSettings);
    s.events.on(Phaser.Scenes.Events.UPDATE, this.onUpdate);
    s.events.once(Phaser.Scenes.Events.SHUTDOWN, this.onShutdown);
    s.events.once(Phaser.Scenes.Events.DESTROY, this.onShutdown);
  }

  play(effect: string, params: Record<string, unknown> = {}): Promise<void> {
    if (!this.scene) { this.warnOnce('no-scene', `[effects] play("${effect}") before attach — ignored`); return Promise.resolve(); }
    const factory = ONE_SHOTS[effect];
    if (!factory) { this.warnOnce(effect, `[effects] unknown effect "${effect}" — resolved immediately`); return Promise.resolve(); }
    let shot: OneShot;
    try { shot = factory(this.host(), params ?? {}); } catch (e) { console.warn(`[effects] "${effect}" failed to start`, e); return Promise.resolve(); }
    return new Promise<void>((resolve) => this.active.push({ shot, name: effect, resolve }));
  }

  setReduceMotion(on: boolean): void {
    this.reduce = !!on;
    for (const l of this.layers) l.setReduceMotion?.(this.reduce);
  }

  // ------------------------------------------------------------------ extras (world / showcase / tests)
  /** Force a lightning flash (+ delayed thunder) now. */
  flash(): void { this.lightningLayer?.flash(); }
  /** Skip every running one-shot to its end state (cinematic skipped). */
  finishAll(): void {
    for (const a of this.active.splice(0)) { try { a.shot.finish(); } catch { /* ignore */ } a.resolve(); if (a.shot.persistent) this.held.push({ shot: a.shot, name: a.name }); else this.safeDestroy(a.shot); }
  }
  /** Drop persistent visuals (glyphs, plates, colour grade) without detaching the weather layers. */
  clearPersistent(): void {
    for (const h of this.held.splice(0)) this.safeDestroy(h.shot);
    this.destroyShared();
  }
  isAttached(): boolean { return this.scene !== null; }
  getState(): EffectsState {
    return {
      attached: !!this.scene, weather: { to: this.track.to, t: +this.track.t.toFixed(2) }, intensity: this.lastIntensity, reduceMotion: this.reduce,
      active: this.active.map((a) => a.name), held: this.held.map((h) => h.name), layers: this.layers.length, flashes: this.flashes, steps: this.stepper.steps,
      particles: this.particles(),
    };
  }
  detach(): void {
    const s = this.scene; if (!s) return;
    s.events.off(Phaser.Scenes.Events.UPDATE, this.onUpdate);
    s.events.off(Phaser.Scenes.Events.SHUTDOWN, this.onShutdown);
    s.events.off(Phaser.Scenes.Events.DESTROY, this.onShutdown);
    bus.off(Events.WEATHER_CHANGE, this.onWeather);
    bus.off(Events.SETTINGS_CHANGE, this.onSettings);
    this.finishAll();
    for (const h of this.held.splice(0)) this.safeDestroy(h.shot);
    this.destroyShared();
    for (const l of this.layers) { try { l.destroy(); } catch { /* scene gone */ } }
    this.layers = []; this.lightningLayer = null; this.lastIntensity = null;
    this.stepper.reset();
    this.scene = null;
  }

  // ------------------------------------------------------------------ internals
  private host(): FxHost {
    const self = this;
    return {
      scene: this.scene as Phaser.Scene, rng: this.shotRng, get reduce() { return self.reduce; },
      sfx: (k, o) => { try { this.sfxFn(k, o); } catch { /* silent */ } },
      duck: (a, ms) => { try { this.duckFn(a, ms); } catch { /* silent */ } },
      shared: this.shared, lightning: () => this.flash(),
    };
  }
  private particles(): Record<string, number> {
    const out: Record<string, number> = {};
    for (const l of this.layers) if (l.stats) Object.assign(out, l.stats());
    for (const a of this.active) if (a.shot.stats) Object.assign(out, a.shot.stats());
    for (const h of this.held) if (h.shot.stats) Object.assign(out, h.shot.stats());
    return out;
  }
  private warnOnce(key: string, msg: string): void { if (this.warned.has(key)) return; this.warned.add(key); console.warn(msg); }
  private safeDestroy(shot: OneShot): void { try { shot.destroy(); } catch { /* already gone */ } }
  private destroyShared(): void {
    for (const [k, v] of this.shared) { if (k.startsWith('plate:')) { try { (v as Phaser.GameObjects.Rectangle).destroy(); } catch { /* gone */ } } }
    this.shared.clear();
  }
  private view(): View {
    const cam = (this.scene as Phaser.Scene).cameras.main;
    return { sx: Math.floor(cam.scrollX), sy: Math.floor(cam.scrollY), w: cam.width, h: cam.height };
  }
  private step(dt: number): void {
    if (!this.scene) return;
    const i = applyReduceMotion(this.track.intensity(), this.reduce); this.lastIntensity = i;
    const view = this.view();
    for (const l of this.layers) { try { l.step(dt, i, view); } catch (e) { this.warnOnce('layer-step', `[effects] layer step failed: ${String(e)}`); } }
    for (let k = this.active.length - 1; k >= 0; k--) {
      const a = this.active[k] as { shot: OneShot; name: string; resolve: () => void };
      let done = false;
      try { done = a.shot.step(dt); } catch (e) { console.warn(`[effects] "${a.name}" step failed`, e); done = true; }
      if (!done) continue;
      this.active.splice(k, 1); a.resolve();
      if (a.shot.persistent) this.held.push({ shot: a.shot, name: a.name }); else this.safeDestroy(a.shot);
    }
    for (const h of this.held) { try { h.shot.step(dt); } catch { /* keep others alive */ } }
  }
}
